"use client";

import type React from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Container from "@components/ui-old/container";
import { useIncentivesQuery } from "@framework/content/get-incentives";
import EventsCardsLoader from "@components/ui-old/loaders/events-loader";
import { AnimatedNoContent } from "@components/other/no-content";
import { CalendarDays, ArrowUpRight, X } from "lucide-react";
import { getPublicBaseUrl } from "@utils/getPublicBaseUrl";
import { useTranslation } from "next-i18next";
import Divider from '@components/ui-old/divider';
const IncentivesCards: React.FC = () => {
  const {
    data,
    isLoading,
    error
  } = useIncentivesQuery();
  const {
    t
  } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  if (isLoading) {
    return <EventsCardsLoader />;
  }
  if (error || !data) {
    return <Container>
        <AnimatedNoContent />
      </Container>;
  }
  const openImageDialog = (src: string, alt: string) => {
    setSelectedImage({
      src,
      alt
    });
    document.body.style.overflow = "hidden";
  };
  const closeImageDialog = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };
  return <Container data-sentry-element="Container" data-sentry-component="IncentivesCards" data-sentry-source-file="incentives-card.tsx">
      <div className="py-16 lg:py-20 px-0 max-w-5xl mx-auto space-y-8">
        <blockquote className="relative backdrop-blur-md bg-gradient-to-r from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 p-8 rounded-xl shadow-sm border border-neutral-100 dark:border-neutral-800 text-xl text-neutral-800 dark:text-neutral-100">
          <svg className="absolute text-neutral-300 dark:text-neutral-700 h-10 w-10 -top-3 -left-2" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="incentives-card.tsx">
            <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" data-sentry-element="path" data-sentry-source-file="incentives-card.tsx" />
          </svg>
          <p className="pl-6">"{data.quote}"</p>
        </blockquote>

        <div className="space-y-6">
          {data.cards.map((card, index) => <div key={card.id}>
              <div className="group border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-neutral-300 dark:hover:border-neutral-500 backdrop-blur-md bg-white dark:bg-neutral-900/70">
                <div className="relative overflow-hidden">
                  <img src={card.image ? `${getPublicBaseUrl()}${card.image}` : card.image ? `${getPublicBaseUrl()}${card.image}` : '/assets/placeholder/collection.svg'} alt={card.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer" onClick={() => openImageDialog(card.image ? `${getPublicBaseUrl()}${card.image}` : '/assets/placeholder/collection.svg', card.title)} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                <div className="p-6">
                  <div className="flex items-center text-neutral-500 mb-3">
                    <CalendarDays className="h-4 w-4 mr-2" />
                    <span className="text-sm">{t('common:text-page-incentives')}</span>
                  </div>

                  <h3 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3 group-hover:text-neutral-700 dark:group-hover:text-neutral-300">
                    {card.title}
                  </h3>

                  <p className="text-neutral-600 dark:text-neutral-400 mb-4">{card.description}</p>

                  <a href={card.linkUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-4 p-2 px-4 py-2 text-neutral-700 dark:text-neutral-100 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-white transition-colors duration-200">
                    <span>{card.linkText}</span>
                    <ArrowUpRight className="h-8 w-8 ml-2 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1 border rounded-2xl p-1" />
                  </a>
                </div>
              </div>
              {index < data.cards.length - 1 && <Divider className="mt-6 border-neutral-100 dark:border-neutral-700" />}
            </div>)}
        </div>
      </div>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="incentives-card.tsx">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={closeImageDialog} style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}>
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.8,
          opacity: 0
        }} transition={{
          type: "spring",
          damping: 25,
          stiffness: 300
        }} className="relative max-w-5xl w-[90vw] max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <motion.button className="absolute top-4 right-4 z-10 p-2 bg-black/60 rounded-full text-white hover:bg-black/80 transition-colors" whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} onClick={e => {
            e.stopPropagation();
            closeImageDialog();
          }}>
                <X className="h-6 w-6" />
              </motion.button>
              <motion.img src={selectedImage.src} alt={selectedImage.alt} className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-2xl" initial={{
            y: 20
          }} animate={{
            y: 0
          }} />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </Container>;
};
export default IncentivesCards;